import { runRules } from '../engines/rules/run';
import { dominantScript } from '../lib/script';
import { PLACEHOLDER, placeholderCounts } from './protect';

export type LangDetect = (text: string, only?: string[]) => string;

const MIN_WORDS_FOR_LOWER_BOUND = 8;
/** franc is unreliable on short text, so the language check only runs on long sentences. */
const MIN_CHARS_FOR_LANG_CHECK = 90;
/** Languages small models most often drift into when they translate by mistake. */
const DRIFT_TARGETS = ['eng', 'cmn', 'spa', 'fra', 'deu', 'por', 'rus'];
/** In these languages capitalization doesn't mark names, so the new-name check would misfire. */
const SKIP_NAME_CHECK = new Set(['deu', 'ltz']);

const STOPWORDS = new Set(
  'about above after again against also although among another because been before being below between both cannot could does doing down during each even every from further have having here into itself just more most much must only other over same should since some such than that their them then there these they this those through under until very were what when where which while will with within without would your yours'.split(' '),
);
/** Below this share of the original's content words, a rewrite has probably changed the meaning. */
const MIN_CONTENT_OVERLAP = 0.25;
const MIN_CONTENT_WORDS = 5;

/** Crude stems: lower-cased first five letters of words four letters or longer, minus stopwords. */
export function contentStems(text: string): Set<string> {
  const out = new Set<string>();
  for (const w of stripTokens(text).toLowerCase().match(/\p{L}[\p{L}\p{M}'’-]*/gu) ?? []) {
    if (w.length < 4 || STOPWORDS.has(w)) continue;
    out.add(w.slice(0, 5));
  }
  return out;
}

function overlap(source: Set<string>, other: Set<string>): number {
  if (!source.size) return 1;
  let hit = 0;
  for (const s of source) if (other.has(s)) hit++;
  return hit / source.size;
}

const words = (text: string) => text.replace(PLACEHOLDER, ' X ').split(/\s+/).filter(Boolean);
const stripTokens = (text: string) => text.replace(PLACEHOLDER, ' ');

/** Names in the original that the rewrite no longer mentions at all. */
export function droppedNames(original: string, rewrite: string): string[] {
  const now = stripTokens(rewrite).toLowerCase();
  return [...new Set(capitalizedNames(original))].filter((n) => !now.includes(n.toLowerCase()));
}

function capitalizedNames(text: string): string[] {
  const names: string[] = [];
  // Must start a word: "reMarkable" is not a name "Markable".
  const re = /(^|[.!?:;]\s+|["“(]\s*)?(?<![\p{L}\p{M}])(\p{Lu}[\p{Ll}\p{M}'’-]+)/gu;
  for (const m of stripTokens(text).matchAll(re)) {
    const atStart = m.index === 0 || m[1] !== undefined;
    if (atStart) continue;
    names.push(m[2]!);
  }
  return names;
}

export interface ValidateOptions {
  /** Flagged stock phrases in the original. Their words don't count toward the meaning overlap. */
  stock?: string[];
  /** Word-overlap meaning checks. Off when an embedding check runs instead. Default on. */
  lexicalMeaning?: boolean;
  /** Leave added stock phrasing to the caller, which ranks it instead of rejecting. */
  softStock?: boolean;
}

export function validateRewrite(
  original: string,
  rewrite: string,
  lang: string,
  detect: LangDetect,
  neighbors: string[] = [],
  opts: ValidateOptions = {},
): string[] {
  const reasons: string[] = [];
  if (!rewrite.trim()) return ['empty rewrite'];
  if (/[\r\n]/.test(rewrite)) reasons.push('line break inside a sentence');

  const expected = placeholderCounts(original);
  const actual = placeholderCounts(rewrite);
  for (const [n, count] of expected) {
    const got = actual.get(n) ?? 0;
    if (got === 0) reasons.push(`dropped locked token [[${n}]]`);
    else if (got > count) reasons.push(`repeated locked token [[${n}]]`);
  }
  for (const n of actual.keys()) if (!expected.has(n)) reasons.push(`invented token [[${n}]]`);

  if (/\d/.test(stripTokens(rewrite))) reasons.push('changed or added a number');

  if (!SKIP_NAME_CHECK.has(lang)) {
    // Split on anything that isn't a letter, so "D.Va" in the original covers "Va" in the rewrite.
    const known = new Set(stripTokens(original).toLowerCase().split(/[^\p{L}\p{M}'’-]+/u).filter(Boolean));
    const bare = (w: string) => w.toLowerCase().replace(/['’]s$/, '');
    const knownBare = new Set([...known].map(bare));
    const added = capitalizedNames(rewrite).filter((n) => !knownBare.has(bare(n)) && n !== 'I');
    if (added.length) reasons.push(`added a name (${added.slice(0, 2).join(', ')})`);
  }

  const before = words(original).length;
  const after = words(rewrite).length;
  if (before >= MIN_WORDS_FOR_LOWER_BOUND && after < before * 0.5) reasons.push('cut too much');
  if (after > Math.max(before * 1.8, before + 5)) reasons.push('grew too long');

  const stock = new Set((opts.stock ?? []).flatMap((p) => [...contentStems(p)]));
  const stemsBefore = new Set([...contentStems(original)].filter((s) => !stock.has(s)));
  const stemsAfter = contentStems(rewrite);
  if (opts.lexicalMeaning !== false && stemsBefore.size >= MIN_CONTENT_WORDS) {
    const kept = overlap(stemsBefore, stemsAfter);
    if (kept < MIN_CONTENT_OVERLAP) reasons.push('changed the meaning');
    else if (neighbors.some((n) => overlap(stemsAfter, contentStems(n)) > overlap(stemsAfter, stemsBefore) + 0.15)) {
      reasons.push('rewrote a neighbouring sentence');
    }
  }

  // Small models given the previous sentence as context sometimes repeat it inside the rewrite.
  if (neighbors.length && repeatsNeighbour(original, rewrite, neighbors)) reasons.push('repeated a neighbouring sentence');

  // A rewrite may not swap one stock phrase for another ("Moreover" → "Furthermore").
  const added = opts.softStock ? [] : stockDiff(original, rewrite, lang).added;
  if (added.length) reasons.push(`added stock phrasing ("${added[0]}")`);

  const plainOriginal = stripTokens(original).trim();
  const plainRewrite = stripTokens(rewrite).trim();
  if (/\?["”’')\]]*$/.test(plainOriginal) && !/\?["”’')\]]*$/.test(plainRewrite)) reasons.push('turned a question into a statement');
  if (plainRewrite.split(/(?<=[.!?…])\s+/).some((part) => drifted(part, lang))) reasons.push('switched language');
  const scriptBefore = dominantScript(plainOriginal);
  const scriptAfter = dominantScript(plainRewrite);
  if (scriptBefore && scriptAfter && scriptBefore !== scriptAfter) {
    reasons.push('switched language');
  } else if (plainOriginal.length >= MIN_CHARS_FOR_LANG_CHECK && plainRewrite.length >= MIN_CHARS_FOR_LANG_CHECK) {
    const candidates = [...new Set([lang, ...DRIFT_TARGETS])];
    const source = detect(plainOriginal, candidates);
    const target = detect(plainRewrite, candidates);
    if (source === lang && target !== 'und' && target !== source) reasons.push('switched language');
  }
  return reasons;
}

/**
 * Function words that mark a language. franc can't judge one short sentence, but a model
 * that slips into French mid-paragraph writes "il", "si", "ce", "qu'il" and almost no English ones.
 */
const FUNCTION_WORDS: Record<string, Set<string>> = {
  eng: new Set('the and is are was were of to that it he she they what his her not with for this be if does did would should there their'.split(' ')),
  fra: new Set('le la les des du est et il elle ils elles si ce qu que sait pas une un dans pour avec vraiment sur au aux cette sont mais nous vous je ne'.split(' ')),
  spa: new Set('el los las es y que una del por para con pero como está muy también lo su sus ella ellos'.split(' ')),
  deu: new Set('der das und ist nicht ein eine mit auch sich auf für dass wenn er sie es ich wir sind'.split(' ')),
  por: new Set('os um uma não que com para mais como mas ele ela isso muito são está'.split(' ')),
  ita: new Set('il lo gli di che è per non una con sono della del nel alla questo anche ma più ha'.split(' ')),
};

export function drifted(sentence: string, lang: string): boolean {
  const own = FUNCTION_WORDS[lang];
  if (!own) return false;
  const tokens = sentence.toLowerCase().split(/[^\p{L}]+/u).filter(Boolean);
  const mine = tokens.filter((t) => own.has(t)).length;
  return Object.entries(FUNCTION_WORDS).some(([code, set]) => {
    if (code === lang) return false;
    const theirs = tokens.filter((t) => set.has(t)).length;
    return theirs >= 3 && theirs > mine * 2;
  });
}

/** Stock phrases the rewrite added, and ones from the original it still uses. */
export function stockDiff(original: string, rewrite: string, lang: string): { added: string[]; left: string[] } {
  const open = (text: string) => runRules(text, lang).flags.filter((f) => !f.fixed);
  const had = new Set(open(original).map((f) => f.ruleId));
  const now = open(rewrite);
  return {
    added: now.filter((f) => !had.has(f.ruleId)).map((f) => f.match.trim()),
    left: now.filter((f) => had.has(f.ruleId)).map((f) => f.match.trim()),
  };
}

function jaccard(a: Set<string>, b: Set<string>): number {
  let shared = 0;
  for (const x of a) if (b.has(x)) shared++;
  const union = a.size + b.size - shared;
  return union ? shared / union : 0;
}

/** Whether one sentence of a multi-sentence rewrite is a copy of a neighbour rather than of the original. */
function repeatsNeighbour(original: string, rewrite: string, neighbors: string[]): boolean {
  const parts = stripTokens(rewrite).split(/(?<=[.!?])\s+/).filter((p) => contentStems(p).size >= 3);
  if (parts.length < 2) return false;
  const own = contentStems(original);
  return parts.some((p) => {
    const stems = contentStems(p);
    return neighbors.some((n) => jaccard(stems, contentStems(n)) >= 0.5 && jaccard(stems, own) < 0.3);
  });
}
